"use client"

import { Text } from "@chakra-ui/react"
import { Box, BoxProps } from "@chakra-ui/react/box"
import { Flex } from "@chakra-ui/react/flex"
import { domAnimation, LazyMotion, m } from "framer-motion"
import React from "react"

type MySkillsCardTitleProps = BoxProps & {
  years: number
  title: string
}

const MySkillsCardTitle: React.FC<MySkillsCardTitleProps> = ({
  title,
  years,
}) => {
  return (
    <Flex
      alignItems="center"
      className="stars"
      display="flex"
      flexDirection="column"
      flexGrow={1}
      gridArea="1 / 1 / 3 / 3"
      justifyContent="space-evenly"
      position="relative"
    >
      <Box
        _after={{
          _dark: {
            bgColor: "var(--gil-colors-secondary-800)",
            boxShadow: ` 0 0 0 0 var(--gil-colors-secondary-600)`,
          },
          bgColor: "var(--gil-colors-secondary-200)",
          borderRadius: "xl",
          boxShadow: ` 0 0 0 0 var(--gil-colors-secondary-500)`,
          content: "''",
          display: "block",
          height: "100%",
          left: "0px",
          position: "absolute",
          top: "0px",
          transition: "transform 0.2s ease-out, box-shadow 0.2s ease-out",
          width: "100%",
          zIndex: "-10",
        }}
        _dark={{
          borderColor: "gray.700",
        }}
        _hover={{
          _after: {
            _dark: {
              boxShadow: `0 0 4px rgb(0 0 0 / 0.122), -3px -3px 0 0 var(--gil-colors-secondary-600)`,
            },
            boxShadow: `0 0 4px rgb(0 0 0 / 0.122), -3px -3px 0 0 var(--gil-colors-secondary-500)`,
            transform: "translate(-3px, -3px)",
          },
          borderColor: "transparent",
        }}
        asChild
        borderRadius={"2xl"}
        flexGrow={1}
        gap="6px"
        height={"100%"}
        overflow="visible"
        position="relative"
        transform="scale(1)"
        transition="transform 0.2s ease-out"
        width={"100%"}
        zIndex="1"
      >
        <LazyMotion features={domAnimation}>
          <m.div
            transition={{ duration: 0.01, ease: "easeOut" }}
            whileHover={{ scale: 1, x: 6, y: 6 }}
          >
            <Box
              _dark={{
                color: "secondary.300",
              }}
              _hover={{
                borderColor: "border.muted",
              }}
              align-items="baseline"
              alignContent="center"
              alignItems="center"
              bgColor={"bg.paper"}
              borderColor={"border.emphasized"}
              borderRadius={"xl"}
              borderWidth="1px"
              className="svg_container"
              color="secondary.400"
              css={{
                "& svg path, & svg ellipse, & svg rect, & svg polygon, & svg circle":
                  {
                    fill: "currentColor",
                  },
                "& text, & tspan": {
                  fill: "secondary.900",
                },
              }}
              display="flex"
              flexDirection="column"
              flexWrap="nowrap"
              fontFamily="heading"
              fontSize="18px"
              fontWeight="bold"
              gap="14px"
              height={"100%"}
              justifyContent="center"
              p="8"
              position="relative"
              width={"100%"}
            >
              <svg viewBox="0 0 108 54" width={124}>
                <path
                  d="M51.36643796,1.63618585c1.07805946-2.18158426,4.1890652-2.18158128,5.2671284.0000098l5.76013647,11.6572519c.42779859.8656804,1.25362605,1.46581181,2.20909556,1.60534286l12.88092428,1.88078178c2.40943414.35181299,3.36931121,3.31398512,1.62404417,5.01205577l-9.31196552,9.06031808c-.69373511.67503659-1.01039967,1.64842396-.84668494,2.60242553l2.19763964,12.79945911c.4119351,2.39944616-2.1060819,4.22980152-4.26132679,3.09759256l-11.51929805-6.05153973c-.85529836-.44933961-1.87695972-.44933961-2.73226181,0l-11.51929805,6.05153973c-2.15524115,1.13220896-4.67326562-.69814641-4.26123341-3.09759256l2.19754252-12.79945911c.16371847-.95400157-.15294609-1.92738894-.84668494-2.60242553l-9.31196365-9.06031808c-1.74526517-1.69807065-.78538809-4.66024278,1.62404417-5.01205577l12.88092615-1.88078178c.95546951-.13953105,1.78129697-.73966245,2.20909556-1.60534286l5.76014021-11.65726171Z"
                  strokeWidth="0"
                />
                <path
                  d="M88.72738576,21.11185362c.72560916-1.48247149,2.8195172-1.48247149,3.5451339,0l3.87700019,7.92166447c.28797043.58820455.84375087.99609101,1.48691576,1.09089513l8.66972689,1.2780333c1.62225379.23913628,2.26779448,2.25207833,1.09336145,3.40594366l-6.26757486,6.15695654c-.46759652.45870044-.68014451,1.12015527-.5704124,1.76846397l1.47990147,8.69779912c.27680037,1.6305578-1.41761762,2.87428897-2.86865492,2.10497628l-7.75330649-4.11238232c-.5757523-.30527234-1.26340135-.30527234-1.83904806,0l-7.75331403,4.11238232c-1.45063756.76931269-3.14545528-.47441848-2.86815713-2.10497628l1.47910953-8.69779912c.11022236-.6483087-.10291392-1.30976352-.56991461-1.76846397l-6.26758241-6.15695654c-1.17472718-1.15386533-.5286887-3.16680738,1.09307484-3.40594366l8.66981739-1.2780333c.64306684-.09480413,1.19894533-.50269058,1.48691576-1.09089513l3.87700773-7.92166447Z"
                  strokeWidth="0"
                />
                <path
                  d="M15.72738576,21.11185362c.72560916-1.48247149,2.8195172-1.48247149,3.5451339,0l3.87700019,7.92166447c.28797043.58820455.84375087.99609101,1.48691576,1.09089513l8.66972689,1.2780333c1.62225379.23913628,2.26779448,2.25207833,1.09336145,3.40594366l-6.26757486,6.15695654c-.46759652.45870044-.68014451,1.12015527-.5704124,1.76846397l1.47990147,8.69779912c.27680037,1.6305578-1.41761762,2.87428897-2.86865492,2.10497628l-7.75330649-4.11238232c-.5757523-.30527234-1.26340135-.30527234-1.83904806,0l-7.75331403,4.11238232c-1.45063756.76931269-3.14545528-.47441848-2.86815713-2.10497628l1.47910953-8.69779912c.11022236-.6483087-.10291392-1.30976352-.56991461-1.76846397l-6.26758241-6.15695654c-1.17472718-1.15386533-.5286887-3.16680738,1.09307484-3.40594366l8.66981739-1.2780333c.64306684-.09480413,1.19894533-.50269058,1.48691576-1.09089513l3.87700773-7.92166447Z"
                  strokeWidth="0"
                />
                <text
                  fill="var(--gil-colors-text-main)"
                  transform={
                    years > 9 ? "translate(44 33.83)" : "translate(47 33.83)"
                  }
                >
                  <tspan x="0" y="0">
                    {years > 9 ? years : "" + years}
                  </tspan>
                </text>
              </svg>

              <Text
                as="b"
                color="fg.muted"
                fontFamily="heading"
                fontSize="xl"
                marginTop={1}
                textAlign="center"
              >
                Years of Experience
                <br />
                {title}
              </Text>
            </Box>
          </m.div>
        </LazyMotion>
      </Box>
    </Flex>
  )
}

export default MySkillsCardTitle
